/**
 * The Catalogue tab's filters and labels, kept pure so they are tested without rendering. The
 * URL is the source of truth for the filters, like the Ledger: a link or Back restores them.
 */
import type { StockedFilter } from '../../services/materialCatalogService';
import { LOCATIONS_PATH } from './utils/siteDrillThrough';

export interface CatalogueFilters {
  search: string;
  typeIds: string[];
  stocked: StockedFilter;
}

export const EMPTY_CATALOGUE_FILTERS: CatalogueFilters = { search: '', typeIds: [], stocked: 'all' };

export const STOCKED_OPTIONS: { value: StockedFilter; label: string }[] = [
  { value: 'all', label: 'All Materials' },
  { value: 'stocked', label: 'Stocked' },
  { value: 'unstocked', label: 'Not Stocked' },
];

const STOCKED_VALUES = STOCKED_OPTIONS.map((o) => o.value);

export function catalogueFiltersFromParams(params: URLSearchParams): CatalogueFilters {
  const stocked = params.get('stocked') as StockedFilter | null;
  return {
    search: params.get('search') ?? '',
    typeIds: (params.get('types') ?? '').split(',').filter(Boolean),
    stocked: stocked && STOCKED_VALUES.includes(stocked) ? stocked : 'all',
  };
}

export function catalogueParams(filters: CatalogueFilters, page = 1): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.typeIds.length) params.set('types', filters.typeIds.join(','));
  if (filters.stocked !== 'all') params.set('stocked', filters.stocked);
  if (page > 1) params.set('page', String(page));
  return params;
}

/** Filters that hide materials (search is shown in its own field, so it is not counted). */
export function activeCatalogueFilterCount(filters: CatalogueFilters): number {
  return (filters.typeIds.length ? 1 : 0) + (filters.stocked !== 'all' ? 1 : 0);
}

/** "1 site", "3 bins" (LANG-08). */
export function countLabel(count: number, one: string, many: string): string {
  return `${count.toLocaleString('en-CA')} ${count === 1 ? one : many}`;
}

/** "W-004-RU (SWITCHBACK RIGHT ARROW)", or whichever half exists. */
export function catalogueMaterialLabel(material: { code: string | null; description: string | null }): string {
  const code = material.code?.trim();
  const description = material.description?.trim();
  if (code && description && code !== description) return `${code} (${description})`;
  return code || description || 'Material';
}

/** The Sites tab narrowed to the sites holding one material (TBL-11). */
export function sitesHoldingPath(materialId: string): string {
  return `${LOCATIONS_PATH}?material=${encodeURIComponent(materialId)}`;
}
